/**
 * Date/Time Formatters Module
 *
 * Provides locale-aware date and time formatting utilities using Intl API.
 * Supports all languages defined in the i18n system.
 *
 * @module i18n/formatters
 * @see Requirements 7.1, 7.2, 7.3, 7.4, 7.5
 */

/**
 * Supported locales for date/time formatting
 * Must match the locales supported by the i18n system
 */
export const SUPPORTED_DATE_LOCALES = ['en', 'zh-TW'] as const;
export type SupportedDateLocale = (typeof SUPPORTED_DATE_LOCALES)[number];

/**
 * Relative time unit thresholds (in milliseconds)
 */
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/**
 * Format a date according to locale preferences
 *
 * @param date - The date to format
 * @param locale - The locale code (e.g., 'en', 'zh-TW')
 * @returns Formatted date string (e.g., "June 15, 2024" or "2024年6月15日")
 *
 * @example
 * formatDate(new Date('2024-06-15'), 'en');    // "June 15, 2024"
 * formatDate(new Date('2024-06-15'), 'zh-TW'); // "2024年6月15日"
 */
export function formatDate(date: Date, locale: SupportedDateLocale): string {
  const safeLocale = SUPPORTED_DATE_LOCALES.includes(locale as SupportedDateLocale) ? locale : 'en';

  return new Intl.DateTimeFormat(safeLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/**
 * Format a time according to locale preferences
 *
 * @param date - The date/time to format
 * @param locale - The locale code (e.g., 'en', 'zh-TW')
 * @returns Formatted time string (e.g., "2:30 PM" or "14:30")
 *
 * @example
 * formatTime(new Date('2024-06-15T14:30:00'), 'en');    // "2:30 PM"
 * formatTime(new Date('2024-06-15T14:30:00'), 'zh-TW'); // "下午2:30"
 */
export function formatTime(date: Date, locale: SupportedDateLocale): string {
  const safeLocale = SUPPORTED_DATE_LOCALES.includes(locale as SupportedDateLocale) ? locale : 'en';

  return new Intl.DateTimeFormat(safeLocale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format a date and time together according to locale preferences
 *
 * @param date - The date/time to format
 * @param locale - The locale code (e.g., 'en', 'zh-TW')
 * @returns Formatted date and time string
 *
 * @example
 * formatDateTime(new Date('2024-06-15T14:30:00'), 'en');
 * // "June 15, 2024 at 2:30 PM"
 */
export function formatDateTime(date: Date, locale: SupportedDateLocale): string {
  const safeLocale = SUPPORTED_DATE_LOCALES.includes(locale as SupportedDateLocale) ? locale : 'en';

  return new Intl.DateTimeFormat(safeLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format a relative time string (e.g., "2 hours ago", "2 小時前")
 *
 * @param date - The date to compare against now
 * @param locale - The locale code (e.g., 'en', 'zh-TW')
 * @param now - Optional reference date (defaults to current time)
 * @returns Formatted relative time string
 *
 * @example
 * const oneHourAgo = new Date(Date.now() - 3600000);
 * formatRelativeTime(oneHourAgo, 'en');    // "1 hour ago"
 * formatRelativeTime(oneHourAgo, 'zh-TW'); // "1 小時前"
 */
export function formatRelativeTime(
  date: Date,
  locale: SupportedDateLocale,
  now: Date = new Date()
): string {
  const safeLocale = SUPPORTED_DATE_LOCALES.includes(locale as SupportedDateLocale) ? locale : 'en';

  const diff = date.getTime() - now.getTime();
  const absDiff = Math.abs(diff);

  const rtf = new Intl.RelativeTimeFormat(safeLocale, {
    numeric: 'auto',
  });

  // Determine the appropriate unit and value
  if (absDiff < MINUTE) {
    // Less than 1 minute - show "just now" or seconds
    const seconds = Math.round(diff / SECOND);
    if (Math.abs(seconds) < 10) {
      // Very recent - use "just now" equivalent
      return rtf.format(0, 'second'); // Returns "now" in most locales
    }
    return rtf.format(seconds, 'second');
  }

  if (absDiff < HOUR) {
    const minutes = Math.round(diff / MINUTE);
    return rtf.format(minutes, 'minute');
  }

  if (absDiff < DAY) {
    const hours = Math.round(diff / HOUR);
    return rtf.format(hours, 'hour');
  }

  if (absDiff < WEEK) {
    const days = Math.round(diff / DAY);
    return rtf.format(days, 'day');
  }

  if (absDiff < MONTH) {
    const weeks = Math.round(diff / WEEK);
    return rtf.format(weeks, 'week');
  }

  if (absDiff < YEAR) {
    const months = Math.round(diff / MONTH);
    return rtf.format(months, 'month');
  }

  const years = Math.round(diff / YEAR);
  return rtf.format(years, 'year');
}

/**
 * Format a short date (for compact displays)
 *
 * @param date - The date to format
 * @param locale - The locale code
 * @returns Short formatted date (e.g., "Jun 15" or "6月15日")
 */
export function formatShortDate(date: Date, locale: SupportedDateLocale): string {
  const safeLocale = SUPPORTED_DATE_LOCALES.includes(locale as SupportedDateLocale) ? locale : 'en';

  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();

  return new Intl.DateTimeFormat(safeLocale, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  }).format(date);
}

/**
 * Smart date formatter that chooses between relative and absolute format
 * based on how recent the date is.
 *
 * - Within 24 hours: relative time (e.g., "2 hours ago")
 * - Within 7 days: relative time (e.g., "3 days ago")
 * - Older: short date format (e.g., "Jun 15")
 *
 * @param date - The date to format
 * @param locale - The locale code
 * @param now - Optional reference date
 * @returns Appropriately formatted date string
 */
export function formatSmartDate(
  date: Date,
  locale: SupportedDateLocale,
  now: Date = new Date()
): string {
  const diff = now.getTime() - date.getTime();

  // Within 7 days - use relative time
  if (diff < WEEK && diff >= 0) {
    return formatRelativeTime(date, locale, now);
  }

  // Older - use short date
  return formatShortDate(date, locale);
}
